import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "subject", "templateId"]
  static values = {
    leadId: Number,
    approvalId: Number
  }
  
  async regenerate(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const originalText = button.textContent
    button.disabled = true
    button.textContent = '🔄 Regenerating...'
    
    try {
      const response = await fetch(`/leads/inbox/${this.leadIdValue}/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          template_id: this.templateIdTarget.value,
          approval_id: this.approvalIdValue
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.contentTarget.value = data.message.content
        if (this.hasSubjectTarget && data.message.subject) {
          this.subjectTarget.value = data.message.subject
        }
        this.showNotification('Message regenerated!', 'success')
      } else {
        this.showNotification(data.error || 'Failed to regenerate', 'error')
      }
    } catch (error) {
      console.error('Regeneration error:', error)
      this.showNotification('Error regenerating message', 'error')
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }
  
  async approve(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    button.disabled = true
    
    try {
      const response = await fetch(`/leads/inbox/${this.leadIdValue}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          approval_id: this.approvalIdValue
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showNotification('Message approved!', 'success')
        this.element.classList.add('approved')
        
        // Reload page to show updated status
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showNotification(data.error || 'Failed to approve', 'error')
        button.disabled = false
      }
    } catch (error) {
      console.error('Approval error:', error)
      this.showNotification('Error approving message', 'error')
      button.disabled = false
    }
  }
  
  async save(event) {
    event.preventDefault()
    
    const updatedContent = this.contentTarget.value
    const updatedSubject = this.hasSubjectTarget ? this.subjectTarget.value : null
    
    const button = event.currentTarget
    const originalText = button.textContent
    button.disabled = true
    button.textContent = '💾 Saving...'
    
    try {
      const response = await fetch(`/leads/inbox/approvals/${this.approvalIdValue}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          approval: {
            personalized_content: updatedContent,
            personalized_subject: updatedSubject
          }
        })
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.showNotification('Changes saved!', 'success')
      } else {
        this.showNotification('Failed to save changes', 'error')
      }
    } catch (error) {
      console.error('Save error:', error)
      this.showNotification('Error saving changes', 'error')
    } finally {
      button.disabled = false
      button.textContent = originalText
    }
  }
  
  async retry(event) {
    event.preventDefault()
    
    this.showNotification('Retrying send...', 'info')
    
    // Would trigger a resend via API
    try {
      const response = await fetch(`/leads/inbox/send_messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          approval_ids: [this.approvalIdValue]
        })
      })
      
      const data = await response.json()
      
      if (data.success && data.results.success.length > 0) {
        this.showNotification('Message sent successfully!', 'success')
        setTimeout(() => window.location.reload(), 1000)
      } else {
        this.showNotification('Failed to send message', 'error')
      }
    } catch (error) {
      console.error('Retry error:', error)
      this.showNotification('Error retrying send', 'error')
    }
  }
  
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
  
  showNotification(message, type) {
    // Dispatch notification event
    const event = new CustomEvent('notification:show', {
      detail: { message, type }
    })
    window.dispatchEvent(event)
    
    // Fallback to console
    console.log(`[${type.toUpperCase()}] ${message}`)
  }
}

